import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "../../ui/dropdown-menu";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../../ui/button";
import { IconCancel } from "@tabler/icons-react";
import { useGlobalStore } from "@/GlobalStore";
import { fetchAccountById, setDefaultAccount } from "@/api/globalApiService";
import { toast } from "sonner";
import ToastContent from "../../common/toast/ToastContent";
import { LuChevronDown, LuCircleCheck, LuLoaderCircle } from "react-icons/lu";
import { Command, CommandGroup, CommandInput, CommandItem } from "../../ui/command";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import AdsPlatformsImage from "@/assets/adsPlatform.svg";
import LoadingSpinner from "../../common/loaders/LoadingSpinner";
import {
  Account,
  EAdAccountPlatform,
  IAccount,
  PlatformType,
  AccountStatus,
} from "@/types";
import { connectAccounts, startGoogleAdsAuth } from "@/api/globalApiService";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import PostOauthDialog from "./PostOAuthDialog";
import googleLogo from "@/assets/icons/google_ads.svg";
import metaLogo from "@/assets/icons/meta.svg";
import linkedinLogo from "@/assets/icons/linkedin-logo.png";
import tiktokLogo from "@/assets/icons/tiktok.png";
import AddAccountDialog from "./AddAccountDialog";
import CombineAccountDialog from "./components/CombineAccountDialog";
import {
  SqDropdownMenu,
  SqDropdownMenuContent,
  SqDropdownMenuGroup,
  SqDropdownMenuItem,
  SqDropdownMenuTrigger,
  SqDropdownMenuSub,
  SqDropdownMenuSubTrigger,
  SqDropdownMenuSubContent,
  SqDropdownMenuPortal,
} from "@/components/squircle-ui/dropdown";
import {
  RiAddLargeLine,
  RiAddLine,
  RiSettingsLine,
  RiGitForkLine,
  RiKey2Line,
  RiMore2Line,
} from "@remixicon/react";
import useAccountsList from "@/hooks/useAccountsList";
import { useParams, useSearchParams } from "react-router-dom";
import { SqButton } from "@/components/squircle-ui/button";
import useOauthConnectionStatus from "@/hooks/useOauthConnectionStatus";

export const SUPPORTED_PLATFORMS = [
  {
    name: "Meta Ads",
    platform: EAdAccountPlatform.FACEBOOK,
    enabled: true,
    description: "(Facebook/Instagram)",
    icon: metaLogo,
  },
  {
    name: "TikTok",
    platform: EAdAccountPlatform.TIKTOK,
    enabled: true,
    description: "(TikTok)",
    icon: tiktokLogo,
  },
  {
    name: "Google Ads",
    platform: EAdAccountPlatform.GOOGLE,
    enabled: true,
    description: "(Google Ads)",
    icon: googleLogo,
  },
  {
    name: "LinkedIn Ads",
    platform: EAdAccountPlatform.LINKEDIN,
    enabled: true,
    description: "(LinkedIn Ads)",
    icon: linkedinLogo,
  },
];

const PLATFORM_ORDER: PlatformType[] = [
  PlatformType.FACEBOOK,
  PlatformType.GOOGLE,
  PlatformType.TIKTOK,
  PlatformType.LINKEDIN,
];

const PLATFORM_INFO: Record<string, { icon: string; label: string }> = {
  [PlatformType.FACEBOOK]: { icon: metaLogo, label: "Meta" },
  [PlatformType.GOOGLE]: { icon: googleLogo, label: "Google" },
  [PlatformType.TIKTOK]: { icon: tiktokLogo, label: "TikTok" },
  [PlatformType.LINKEDIN]: { icon: linkedinLogo, label: "LinkedIn" },
};

const getAccountPlatforms = (item: Account) => {
  if (item.type === "MERGED" && item.child_accounts?.length) {
    const unique = [...new Set(item.child_accounts.map((c) => c.platform))];
    return unique.sort((a, b) => PLATFORM_ORDER.indexOf(a) - PLATFORM_ORDER.indexOf(b));
  }
  return [item.platform];
};

interface Props {
  platform?: string;
  expanded?: boolean;
  onAnyDialogOpenChange?: (open: boolean) => void;
}
const AccountsDropdown = ({ expanded = false, onAnyDialogOpenChange }: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const {
    accountList,
    setAccountList,
    selectedAccount,
    noAccountsAvailable,
    setSelectedAccount,
  } = useGlobalStore();
  const {
    isInitializingAccounts,
    isLoading,
    onScrollNextPageLoader,
    listContainerRefCallback,
    searchQuery,
    setSearchQuery,
    effectiveAccountList,
    handleAccountChange,
  } = useAccountsList();

  const { accountId } = useParams();
  useEffect(() => {
    if (!accountId || selectedAccount?.id === accountId) return;
    fetchAccountById(accountId)
      .then((account) => {
        // selecting account on params changes
        setSelectedAccount(account);
      })
      .catch((error) => {
        console.error(error);
        if (accountList?.[0]) {
          // fallback account change handle
          handleAccountChange(accountList?.[0]); // change account
          toast.message(
            <ToastContent
              icon={LuCircleCheck}
              headingPrefix="Switched to"
              headingHighlight={`"${accountList?.[0]?.name}"`}
              subheadingPrefix="Access to the previous account is unavailable."
              isError={false}
            />,
          );
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultAccountDialog = useMemo(
    () => searchParams.get("add_account"),
    [searchParams],
  );

  const [openAddAccountDialog, setOpenAddAccountDialog] = useState(
    defaultAccountDialog === "true",
  );

  const confirmAddAccount = async (platform: EAdAccountPlatform) => {
    setOpenAddAccountDialog(false);
    setSearchParams({});
    try {
      const authUrl = await startGoogleAdsAuth(window.location.href, platform);
      window.open(authUrl, "_self");
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (searchParams.get("add_account") === "true") {
      setOpenAddAccountDialog(true);
    } else {
      setOpenAddAccountDialog(false);
    }
  }, [searchParams]);

  const handleAddAccountClose = () => {
    setSearchParams({});
    setOpenAddAccountDialog(false);
  };

  const oauthPlatform = useMemo(() => searchParams.get("oauth_platform"), [searchParams]);

  const [openPostOauthDialog, setOpenPostOauthDialog] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [connectingAccounts, setConnectingAccounts] = useState<boolean>(false);
  const [openCombineAccountDialog, setOpenCombineAccountDialog] = useState(false);
  const [connectExistingOpen, setConnectExistingOpen] = useState(false);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [kebabOpenId, setKebabOpenId] = useState<string | null>(null);

  const handleSetDefault = async (account: Account) => {
    const isAlreadyDefault = account.default === true;
    const accountId = isAlreadyDefault ? null : account.id;
    setSettingDefaultId(account.id);
    try {
      await setDefaultAccount(accountId);
      // Update the default flag in the account list
      const updatedList = accountList.map((a) => ({
        ...a,
        default: a.id === account.id ? !isAlreadyDefault : false,
      }));
      setAccountList(updatedList);
      // Update selected account if it was affected
      if (selectedAccount) {
        setSelectedAccount({
          ...selectedAccount,
          default: selectedAccount.id === account.id ? !isAlreadyDefault : false,
        });
      }
      toast.message(
        <ToastContent
          headingPrefix={
            isAlreadyDefault ? "Default account removed" : "Default account set"
          }
          subheadingPrefix={
            isAlreadyDefault
              ? `"${account.name}" is no longer the default account.`
              : `"${account.name}" has been set as the default account.`
          }
        />,
      );
    } catch {
      toast.message(
        <ToastContent
          headingPrefix="Failed to update default account"
          subheadingPrefix="Please try again."
          isError
        />,
      );
    } finally {
      setSettingDefaultId(null);
      setKebabOpenId(null);
    }
  };

  // Initialize OAuth connection status handling (query parsing + polling)
  useOauthConnectionStatus();

  const handleAddAccountFromPostOauth = () => {
    setSearchParams({ add_account: "true" });
  };

  const handlePlatformSelect = (platform: EAdAccountPlatform) => {
    setSelectedPlatform(platform);
    setOpenPostOauthDialog(true);
  };

  const onAccountConnect = async (accounts: IAccount[]) => {
    setConnectingAccounts(true);
    try {
      const update_res = await connectAccounts(accounts.map((item) => item.id));
      if (update_res.status === 200) {
        // Fetch accounts after successful connection
        const newAccounts = await Promise.all(
          update_res.data.account_ids.map((id: string) => fetchAccountById(id)),
        );
        if (newAccounts) {
          setAccountList([...accountList, ...newAccounts]);
          handleAccountChange(newAccounts[0], true);
        }
        accounts.forEach((item) => {
          toast.message(
            <ToastContent
              headingPrefix="Account Connected Successfully"
              subheadingPrefix={`Account "${item.name}" has been connected successfully.`}
            />,
          );
        });
      }
    } catch (error) {
      console.error("Error connecting accounts:", error);
    } finally {
      setOpenPostOauthDialog(false);
      setSelectedPlatform(null);
      setConnectingAccounts(false);
    }
  };

  useEffect(() => {
    if (oauthPlatform || selectedPlatform) {
      setOpenPostOauthDialog(true);
    }
  }, [oauthPlatform, selectedPlatform]);

  return (
    <>
      <DropdownMenu
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          onAnyDialogOpenChange?.(isOpen);
        }}
        modal={true}
      >
        <DropdownMenuTrigger asChild>
          <Button className="p-0 border-0 h-26 transition-all duration-300 bg-transparent flex justify-start hover:bg-transparent shadow-none hover:squircle hover:squircle-radius-[6px] hover:squircle-smooth-[1] hover:squircle-fill-[#273d78]">
            <div className="flex items-center max-w-full overflow-hidden w-full">
              {isLoading &&
              isInitializingAccounts &&
              effectiveAccountList.length === 0 ? (
                <span className="h-[26px] w-[26px] flex-shrink-0 squircle squircle-radius-[6px] squircle-smooth-[1] squircle-fill-[white] flex items-center justify-center">
                  <LoadingSpinner className="h-4 w-4 text-brand-primary-800" />
                </span>
              ) : noAccountsAvailable ? (
                <span className="h-[26px] w-[26px] flex-shrink-0 squircle squircle-radius-[6px] squircle-smooth-[1] squircle-fill-[white] flex items-center justify-center">
                  <IconCancel size={16} color="#FF450099" />
                </span>
              ) : (
                <>
                  {selectedAccount?.additional_info?.ad_account_icon_url ||
                  selectedAccount?.additional_info?.ad_information?.account_icon_url ? (
                    <img
                      src={
                        selectedAccount?.additional_info?.ad_account_icon_url ??
                        selectedAccount?.additional_info?.ad_information?.account_icon_url
                      }
                      className="h-[26px] w-[26px] flex-shrink-0 squircle-mask squircle-radius-[6px] squircle-smooth-[1] bg-white"
                    />
                  ) : (
                    <span className="h-[26px] w-[26px] flex-shrink-0 squircle squircle-radius-[6px] squircle-smooth-[1] squircle-fill-[white] flex items-center justify-center">
                      <p className="relative top-[1px] text-brand-primary-800 text-[14px] font-bold">
                        {selectedAccount?.name[0]?.toUpperCase()}
                      </p>
                    </span>
                  )}
                </>
              )}

              <div
                className={cn(
                  "flex-1 flex flex-col items-start justify-center min-w-0 overflow-hidden transition-opacity duration-300",
                  expanded ? "opacity-100 ml-2" : "opacity-0 ml-0 pointer-events-none",
                )}
              >
                {expanded && (
                  <p className="w-full text-left text-[#FFF] text-[11px] font-normal leading-normal overflow-hidden text-ellipsis whitespace-nowrap">
                    {isLoading &&
                    isInitializingAccounts &&
                    effectiveAccountList.length === 0
                      ? "Loading..."
                      : noAccountsAvailable
                        ? "No accounts"
                        : selectedAccount?.name}
                  </p>
                )}
              </div>
              {expanded && (
                <LuChevronDown className="h-[16px] w-[16px] shrink-0 text-[#808BAC] ml-auto mr-1" />
              )}
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[400px] rounded-[12px] relative p-[8px] z-[40]"
          collisionPadding={10}
        >
          <Command
            className="relative max-h-[500px]"
            filter={(value, search) => {
              // Since value is now the label, we can use it directly
              const decodedValue = decodeURIComponent(value);
              return decodedValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
            }}
            shouldFilter={false}
          >
            <CommandInput
              wrapperClass="!border-0 rounded-[8px] bg-selected-gray"
              className="py-1 h-8 text-xs text-brand-primary-new placeholder:text-[#A0ABBC] placeholder:text-xs placeholder:font-normal"
              value={searchQuery ?? ""}
              placeholder="Search"
              onValueChange={(value) => setSearchQuery(value)}
              disabled={noAccountsAvailable}
            />
            {effectiveAccountList.length > 0 && (
              <CommandGroup
                ref={listContainerRefCallback}
                onScroll={onScrollNextPageLoader}
                className="overflow-y-auto mt-2 px-0 mx-0"
              >
                {effectiveAccountList.map((item, index) => {
                  const platforms = getAccountPlatforms(item);
                  const accountIcon =
                    item.additional_info?.ad_account_icon_url ??
                    item.additional_info?.ad_information?.account_icon_url ??
                    item.logos?.[0]?.url;
                  return (
                    <CommandItem
                      key={`${item.id}-${index}`}
                      value={encodeURIComponent(item.name)}
                      onSelect={() => {
                        if (kebabOpenId) return;
                        const _item = effectiveAccountList.find((i) => i.id === item.id);
                        if (_item && selectedAccount && _item.id === selectedAccount.id) {
                          setOpen(false);
                          onAnyDialogOpenChange?.(false);
                          return;
                        }
                        if (_item) {
                          handleAccountChange(_item);
                          setOpen(false);
                          onAnyDialogOpenChange?.(false);
                        }
                      }}
                      className={cn(
                        "cursor-pointer rounded-[8px] gap-2 h-auto px-[8px] py-2.5 items-center text-brand-primary-new",
                        "hover:bg-[#F6F7F8]",
                        "[&:hover_.kebab-btn]:opacity-100",
                      )}
                    >
                      <div className="flex w-full gap-3">
                        {/* Account icon */}
                        <div className="relative shrink-0 self-center">
                          {accountIcon ? (
                            <img
                              src={accountIcon}
                              alt=""
                              className="h-[26px] w-[26px] squircle-mask squircle-radius-[6px] bg-white object-cover"
                            />
                          ) : (
                            <span className="h-[26px] w-[26px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-fill-[#F0F2F5] flex items-center justify-center">
                              {item.type === "MERGED" ? (
                                <RiGitForkLine size={18} color="#FF4500" />
                              ) : (
                                <p className="text-brand-primary-800 text-[16px] font-bold">
                                  {item.name[0]?.toUpperCase()}
                                </p>
                              )}
                            </span>
                          )}
                          {/* Status dot — bottom-right, overlapping the icon */}
                          <span
                            className={cn(
                              "absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white",
                              item.status === AccountStatus.ACTIVE
                                ? "bg-[#039F54]"
                                : "bg-[#EF2B00]",
                            )}
                          />
                        </div>

                        {/* Name + platforms/default */}
                        <div className="flex-1 min-w-0">
                          {/* Top row: name + kebab */}
                          <div className="flex items-start justify-between">
                            <TooltipProvider delayDuration={300}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className={cn(
                                      "truncate font-inter text-[13px] leading-[18px] tracking-[0.02em] text-left text-brand-primary-new",
                                      selectedAccount && selectedAccount.id === item.id
                                        ? "font-medium"
                                        : "font-normal",
                                    )}
                                  >
                                    {item.name}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">{item.name}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <Popover
                              open={kebabOpenId === item.id}
                              onOpenChange={(isOpen) => {
                                setKebabOpenId(isOpen ? item.id : null);
                              }}
                            >
                              <PopoverTrigger asChild>
                                <button
                                  className={cn(
                                    "kebab-btn shrink-0 transition-colors -mr-1",
                                    kebabOpenId === item.id ? "opacity-100" : "opacity-0",
                                  )}
                                  onClick={(e) => e.stopPropagation()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                >
                                  <RiMore2Line size={16} className="text-[#617390]" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                side="right"
                                align="start"
                                sideOffset={8}
                                className="w-auto p-1 rounded-[8px]"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-[6px] hover:bg-[#F6F7F8] transition-colors w-full text-left disabled:opacity-50"
                                  disabled={settingDefaultId === item.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleSetDefault(item);
                                  }}
                                >
                                  {settingDefaultId === item.id ? (
                                    <LuLoaderCircle
                                      className="animate-spin shrink-0"
                                      size={14}
                                      color="#617390"
                                    />
                                  ) : (
                                    <RiSettingsLine
                                      size={14}
                                      className="shrink-0 text-[#617390]"
                                    />
                                  )}
                                  <span className="text-[12px] font-normal text-brand-primary-new/80 whitespace-nowrap">
                                    {item.default ? "Remove default" : "Set as default"}
                                  </span>
                                </button>
                              </PopoverContent>
                            </Popover>
                          </div>
                          {/* Bottom row: platforms + default badge */}
                          <div className="flex items-center justify-between mt-0.5">
                            <div className="flex items-center gap-2">
                              {platforms.map((platform) => {
                                const info = PLATFORM_INFO[platform];
                                if (!info) return null;
                                return (
                                  <div key={platform} className="flex items-center gap-1">
                                    <img
                                      src={info.icon}
                                      alt={info.label}
                                      className="w-3.5 h-3.5 object-contain"
                                    />
                                    <span className="text-[11px] text-[#617390] font-normal">
                                      {info.label}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            {item.default && (
                              <span className="flex items-center gap-1 text-[11px] font-normal text-[#67749B] shrink-0">
                                <RiSettingsLine size={12} className="shrink-0" />
                                Default
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {noAccountsAvailable ? (
              <div className="w-full h-full flex flex-col items-center justify-center p-2 my-16">
                <img src={AdsPlatformsImage} />
                <div className="font-medium text-[14px] leading-[100%] tracking-[-1%] text-center text-brand-primary-new mt-8">
                  Start by connecting your Ad account
                </div>
                <p className="font-normal text-[12px] leading-[100%] tracking-[0%] text-center text-[#617390] mt-2">
                  Click the button below to connect your <br /> account to the platform
                </p>
              </div>
            ) : (
              !isLoading &&
              effectiveAccountList.length === 0 && (
                <div className="py-6 flex justify-center text-sm text-gray-500">
                  No results found
                </div>
              )
            )}

            {isLoading && (
              <div className="h-12 flex justify-center items-center">
                <LuLoaderCircle className="animate-spin" color="#FF4500" size={18} />
              </div>
            )}
            <div className="bg-white pb-1">
              <div className="w-full flex justify-end gap-0">
                <SqDropdownMenu>
                  <SqDropdownMenuTrigger className="w-fit squircle-outline-[0px] p-0">
                    <SqButton
                      size={"md"}
                      hasOutline={false}
                      bgColor="#FF4500"
                      hoverBgColor="#F14707"
                      variant={"default"}
                      shadow={"sm"}
                    >
                      <RiAddLine size={14} color="#FFFFFFCC" className="shrink-0 mr-1" />
                      <span className="relative top-[1px] font-normal text-white text-[11px]">
                        Add Account
                      </span>
                    </SqButton>
                  </SqDropdownMenuTrigger>
                  <SqDropdownMenuContent side="right" align="start" radius={12}>
                    <SqDropdownMenuGroup>
                      <SqDropdownMenuItem
                        className="gap-2"
                        onClick={() => setSearchParams({ add_account: "true" })}
                      >
                        <RiAddLargeLine size={14} color="#9aa3bd" className="shrink-0" />
                        <span className="font-normal text-[12px] text-brand-primary-new/80 translate-y-[1px]">
                          Add new account
                        </span>
                      </SqDropdownMenuItem>

                      <SqDropdownMenuSub
                        open={connectExistingOpen}
                        onOpenChange={(open) => {
                          // Only allow closing via onOpenChange
                          if (!open) {
                            setConnectExistingOpen(false);
                          }
                        }}
                      >
                        <SqDropdownMenuSubTrigger
                          className="gap-2"
                          hideChevron
                          onClick={() => setConnectExistingOpen(true)}
                        >
                          <RiKey2Line size={14} color="#9aa3bd" className="shrink-0" />
                          <span className="font-normal text-[12px] text-brand-primary-new/80 translate-y-[1px]">
                            Connect existing
                          </span>
                        </SqDropdownMenuSubTrigger>
                        <SqDropdownMenuPortal>
                          <SqDropdownMenuSubContent
                            className={cn(
                              "w-[200px] squircle squircle-radius-[8px] squircle-smooth-[1] squircle-fill-[white] transition-all rounded-lg bg-white border border-gray-200",
                              "drop-shadow-md",
                            )}
                            sideOffset={10}
                            alignOffset={10}
                          >
                            {SUPPORTED_PLATFORMS.map((platform) => (
                              <SqDropdownMenuItem
                                key={platform.platform}
                                onClick={() =>
                                  platform.enabled &&
                                  handlePlatformSelect(platform.platform)
                                }
                                className={cn(
                                  "flex items-center gap-2 h-9",
                                  !platform.enabled && "opacity-50 cursor-not-allowed",
                                )}
                                disabled={!platform.enabled}
                              >
                                <div className="w-4 h-4 flex items-center justify-center">
                                  {platform.icon ? (
                                    <img
                                      src={platform.icon}
                                      alt={`${platform.name} Logo`}
                                      className="w-4 h-4 object-contain"
                                    />
                                  ) : (
                                    <div className="w-4 h-4 bg-gray-200 rounded-sm" />
                                  )}
                                </div>
                                <div className="flex flex-col">
                                  <span>{platform.name}</span>
                                  {platform.description && (
                                    <span className="text-xs text-gray-500">
                                      {platform.description}
                                    </span>
                                  )}
                                </div>
                              </SqDropdownMenuItem>
                            ))}
                          </SqDropdownMenuSubContent>
                        </SqDropdownMenuPortal>
                      </SqDropdownMenuSub>

                      <SqDropdownMenuItem
                        className="gap-2"
                        onClick={() => setOpenCombineAccountDialog(true)}
                      >
                        <RiGitForkLine size={14} color="#9aa3bd" className="shrink-0" />
                        <span className="font-normal text-[12px] text-brand-primary-new/80 translate-y-[1px]">
                          Combine Accounts
                        </span>
                      </SqDropdownMenuItem>
                    </SqDropdownMenuGroup>
                  </SqDropdownMenuContent>
                </SqDropdownMenu>
              </div>
            </div>
          </Command>
        </DropdownMenuContent>
      </DropdownMenu>
      <AddAccountDialog
        open={openAddAccountDialog}
        onOpenChange={(open) => {
          handleAddAccountClose();
          onAnyDialogOpenChange?.(open);
        }}
        onConfirm={confirmAddAccount}
      />
      {/* Post OAuth Dialog */}
      {(oauthPlatform || selectedPlatform) && (
        <PostOauthDialog
          open={openPostOauthDialog}
          onOpenChange={(open) => {
            setOpenPostOauthDialog(open);
            if (!open) {
              setSelectedPlatform(null);
              setSearchParams({});
            }
            onAnyDialogOpenChange?.(open);
          }}
          platform={oauthPlatform || selectedPlatform || ""}
          onAddAccount={handleAddAccountFromPostOauth}
          onConnectAccounts={onAccountConnect}
          connectingAccounts={connectingAccounts}
        />
      )}
      {/* Combine Account Dialog */}
      <CombineAccountDialog
        open={openCombineAccountDialog}
        onOpenChange={setOpenCombineAccountDialog}
      />
    </>
  );
};

export default AccountsDropdown;
